// ANCHOR React
import { useState } from 'react';

// ANCHOR Next
import { useRouter } from 'next/router';

// ANCHOR Base
import { Button, KIND, SIZE } from 'baseui/button';

// ANCHOR Models
import { useCourseDataId, useCourseDataConsumablePeriod } from '@lyon/scoped-models/CourseData';
import { ProviderCheckManager } from '@lyon/scoped-models/ProviderCheckManager';
import { useCourseModulesData } from '@lyon/scoped-models/CourseModulesData';
import { ConfirmationModalVisibility } from '@lyon/scoped-models/confirmation-modal/ConfirmationModalVisibility';
import {
  useCourseStudentCheckID,
  useCourseStudentCheckIsCourseStudent,
  useCourseStudentStartedAt,
} from '@lyon/scoped-models/course-student/CourseStudentCheck';

// ANCHOR API
import { updateCourseStudentStartTime } from '@lyon/utils/api/course-student';

// ANCHOR Graph State
import { useAuthToken } from '@lyon/nodes/firebase/auth/auth-token-node';

// ANCHOR Hooks
import { useSentryExceptionHandler } from '@lyon/utils/hooks/useSentryExceptionHandler';
import { usePromise } from '@lyon/utils/hooks/usePromise';

// ANCHOR Components
import { constant } from '@lyon/components/utils/constant';
import { LyonConfirmationModal } from '@lyon/components/confirmation-modal/LyonConfirmationModal';
import { LyonBannerToast } from '@lyon/components/banner-toast/LyonBannerToast';

// ANCHOR Constants
import {
  LABEL,
  CONTINUE_LABEL,
  TYPE,
  PROMPT,
  DESCRIPTION,
} from './constants';

// ANCHOR Styles
import { BUTTON } from './styles';

export const LyonCourseStartButton = constant(() => {
  const router = useRouter();
  const inviteParam = router.query.invite;
  const fromParam = router.query.from;
  const catchException = useSentryExceptionHandler();
  const token = useAuthToken();

  const isProvider = ProviderCheckManager.useSelector((state) => state.data);
  const show = ConfirmationModalVisibility.useSelector((state) => state.show);

  const courseId = useCourseDataId();
  const consumablePeriod = useCourseDataConsumablePeriod();
  const modules = useCourseModulesData();
  const startedAt = useCourseStudentStartedAt();
  const studentId = useCourseStudentCheckID();
  const isStudent = useCourseStudentCheckIsCourseStudent();

  const [loading, setLoading] = useState(false);
  const mounted = usePromise([courseId, modules, token, studentId]);

  const onClick = async () => {
    setLoading(true);

    try {
      // Course student
      if (!isProvider && !startedAt && studentId && token) {
        await mounted(updateCourseStudentStartTime({
          studentId,
          token,
        }));
      }

      if (courseId) {
        const firstModuleWithItem = modules?.find((item) => item.moduleItems.length > 0);
        const firstModuleItem = firstModuleWithItem?.moduleItems[0];

        await mounted(router.push(
          `/course/${courseId}/content${firstModuleItem ? `/${firstModuleItem.id}` : ''}`,
        ));
      }
    } catch (error) {
      catchException(error);
    }

    setLoading(true);
  };

  if (startedAt) {
    return (
      <>
        <LyonBannerToast
          content={'Looks like you\'ve already started this course. You can continue by clicking "Continue Course".'}
          show={inviteParam === 'success' || fromParam === 'invite'}
          isSuccess
        />
        <LyonBannerToast
          content={'Looks like you\'re access to this course has already expired.'}
          show={!isStudent}
        />
        <Button
          kind={KIND.primary}
          size={SIZE.default}
          isLoading={loading || modules == null}
          onClick={onClick}
          overrides={BUTTON}
          disabled={!isStudent}
        >
          {CONTINUE_LABEL}
        </Button>
      </>
    );
  }

  return (
    <>
      <LyonBannerToast
        content={'Looks like you\'re access to this course has already expired.'}
        show={!isStudent}
      />
      <LyonBannerToast
        content={'Success! 🎉 You\'re now registered to the course. You can begin by clicking "Start Course".'}
        show={inviteParam === 'success'}
        isSuccess
      />
      <LyonBannerToast
        content={'Looks like you\'re already registered to this course. You can begin by clicking "Start Course".'}
        show={fromParam === 'invite'}
        isSuccess
      />
      <Button
        kind={KIND.primary}
        size={SIZE.default}
        isLoading={modules == null}
        onClick={consumablePeriod ? show : onClick}
        overrides={BUTTON}
        disabled={!isStudent}
      >
        {LABEL}
      </Button>
      {consumablePeriod && (
        <LyonConfirmationModal
          contentType={TYPE}
          prompt={PROMPT}
          description={DESCRIPTION(consumablePeriod)}
          asyncCallback={onClick}
          isStrict={false}
          loading={loading}
        />
      )}
    </>
  );
});
